import { ReactRenderer } from '@tiptap/react'
import Suggestion from '@tiptap/suggestion'

import type { MentionDto } from '@/entities/mentions'
import { updateTiptapSuggestionsPosition } from '@/entities/tiptap-suggestions/lib/update-tiptap-suggestions-position'

import type {
  VariablesSuggestionsCommandReturn,
  VariablesSuggestionsHandle,
} from './variables.config'
import { variablesExtensionConfig } from './variables.config'
import { VariablesSuggestionsList } from './VariablesSuggestionsList'

import type { Editor } from '@tiptap/core'

export function createVariablesSuggestion(options: {
  name: string
  editor: Editor
}) {
  return Suggestion<MentionDto, VariablesSuggestionsCommandReturn>({
    pluginKey: variablesExtensionConfig.pluginKey,
    editor: options.editor,
    char: variablesExtensionConfig.character,
    allowSpaces: false,
    startOfLine: false,

    render() {
      let component:
        | ReactRenderer<
            VariablesSuggestionsHandle,
            React.ComponentProps<typeof VariablesSuggestionsList>
          >
        | undefined

      return {
        onStart(props) {
          if (!props.editor.isFocused) return

          component = new ReactRenderer(VariablesSuggestionsList, {
            props: props,
            editor: props.editor,
          })

          if (component.element instanceof HTMLElement) {
            component.element.style.position = 'absolute'

            // Append to body to allow positioning outside the editor boundaries
            document.body.appendChild(component.element)

            updateTiptapSuggestionsPosition(props.editor, component.element)
          }
        },
        onUpdate(props) {
          component?.updateProps(props)

          if (component?.element instanceof HTMLElement) {
            updateTiptapSuggestionsPosition(props.editor, component.element)
          }
        },
        onKeyDown(props) {
          if (props.event.key === 'Escape') {
            component?.destroy()
            return true
          }

          return component?.ref?.onKeyDown(props) ?? false
        },
        onExit() {
          component?.destroy()
        },
      }
    },

    command({ editor, range, props }) {
      const { variable } = props

      editor
        .chain()
        .focus()
        .insertContentAt(range, [
          {
            type: options.name,
            attrs: {
              variableKey: variable.key,
              fallbackValue: variable.value,
            },
          },
        ])
        .run()
    },
  })
}
